import { Injectable } from '@angular/core';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { KeplerService } from 'app/kepler/services/kepler.service';
import { tap } from 'rxjs/operators';
import { DEFAULT_RESPONSE_TABLE } from 'app/kepler/assets/constants';

@Injectable()
export class KeplerFederationRankingListService {
  userRole: any;
  constructor(
    public keplerService: KeplerService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: 'position',
      columns: this.getColumns(),
      load: (props) => this.fetch(props, dataSource).subscribe(() => dataSource.refresh()),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { matchId, league, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      league: league == 'all' ? '' : league,
      $filter
    };
    return this.keplerService.getMatchFederationRanking(matchId, paginationOptions).pipe(
      tap((response) => {
        dataSource.docs = response?.docs || DEFAULT_RESPONSE_TABLE.docs;
        dataSource.total = response?.total || DEFAULT_RESPONSE_TABLE.total;
        dataSource.limit = response?.limit || DEFAULT_RESPONSE_TABLE.limit;
        dataSource.page = response?.page || DEFAULT_RESPONSE_TABLE.page;
        dataSource.pages = response?.pages || DEFAULT_RESPONSE_TABLE.pages;
      })
    );
  }

  getCustomFilters() {
    return {
      'indicators.co2': (filterBuilder, value) => {
        if (value) return filterBuilder.and(`indicators/co2 ge ${value}`);
      },
      'indicators.population': (filterBuilder, value) => {
        if (value) return filterBuilder.and(`indicators/population ge ${value}`);
      }
    };
  }

  getColumns() {
    const columns = [
      {
        source: 'position',
        label: '#',
        dataType: DataTypes.number
      },
      {
        source: 'federation',
        label: this.translate.instant('nouns.name'),
        dataType: DataTypes.String,
        sortable: true
      },
      {
        source: 'league',
        label: this.translate.instant('kepler.league.name'),
        dataType: DataTypes.String,
        sortable: true
      },
      {
        source: 'indicators.population',
        label: this.translate.instant('kepler.ranking.avgPopulation'),
        dataType: DataTypes.number,
        valueParser: (value) => {
          if (value) return Math.round(value);
          return '-';
        }
      },
      {
        source: 'indicators.co2',
        label: this.translate.instant('kepler.ranking.avgCo2'),
        dataType: DataTypes.number,
        valueParser: (value) => {
          if (value) return Math.round(value);
          return '-';
        }
      }
    ];
    return columns;
  }
}
